import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Copy, MessageCircle, Pencil, RotateCcw, Save, Megaphone } from "lucide-react";

const INTRO = `🌿 *{STUDYO} Online Ders Programı'na hoş geldiniz!* 🌿

Merhaba arkadaşlar 👋
Derslerimizi (Grup Dersi, Düet ve Özel Ders) artık *online rezervasyon sistemi* ile düzenliyoruz. Yerinizi telefonunuzdan birkaç saniyede ayırabileceksiniz. Her hafta yeni ders programını bu grupta paylaşacağım. 📅

🔗 *Rezervasyon linkimiz:*
{LINK}
_(Linki kaydetmenizi öneririm, her hafta aynı link geçerli.)_

━━━━━━━━━━━━━━
📲 *NASIL REZERVASYON YAPILIR?*

1️⃣ Linke tıklayın. Önümüzdeki 7 günün dersleri görünecek.
2️⃣ Gün seçin ve katılmak istediğiniz derse dokunun. Her derste türü yazar: *Grup Dersi*, *Düet* veya *Özel Ders*.
3️⃣ *Telefon numaranızı* yazıp "Devam" deyin.
 ✅ *Daha önce kayıtlıysanız* sistem sizi numaranızdan bulur, adınız ekranda görünür (ör. "Ayşe Y.").
 🆕 *İlk kez geliyorsanız* ad, soyad, cinsiyet ve doğum tarihinizi girin (e-posta isteğe bağlı), kaydınız otomatik açılır.
4️⃣ *Nasıl geleceğinizi* seçin: kurumsal kartınız, *Paketim var* ya da *Paket almak istiyorum*.
 💡 Paketiniz yoksa da yerinizi ayırabilirsiniz. Paketinizi derse geldiğinizde stüdyodan alırsınız.
5️⃣ "Rezervasyonu Tamamla" dediğinizde yeriniz ayrılmış olur. 🎉

⚠️ *Lütfen her seferinde aynı telefon numarasını kullanın.* Böylece kaydınız tek hesapta toplanır, paketiniz ve geçmişiniz doğru işlenir.
📌 *Aynı güne yalnızca 1 rezervasyon yapabilirsiniz.* O gün başka bir dersiniz/randevunuz varsa sistem sizi uyarır ve saatini gösterir.

━━━━━━━━━━━━━━
❌ *İPTAL NASIL YAPILIR?*

1️⃣ Aynı linki açın.
2️⃣ Sayfanın altındaki *"Rezervasyonlarım / İptal"* butonuna dokunun.
3️⃣ Telefon numaranızı yazın. Rezervasyonlarınız listelenir.
4️⃣ İptal etmek istediğiniz dersin yanındaki *"İptal Et"* butonuna basın.

⏰ İptaller *dersten en geç {IPTAL_SAAT} saat önce* yapılabilir. Daha geç iptal için lütfen stüdyoyu arayın.
🙏 Gelemeyecekseniz mutlaka iptal edin. Böylece yeriniz bekleyen bir arkadaşımıza geçer. Haber vermeden gelinmeyen dersler sistemde *"Gelmedi"* olarak kaydedilir.

━━━━━━━━━━━━━━
🔔 *DERS DOLUYSA?*

Dolu derste *"Yer açılırsa haber ver"* butonuna basarak bekleme listesine girebilirsiniz. Yer açılınca size WhatsApp'tan haber veririz.
🔥 *"Son 1 yer!"* yazan derslerde acele edin 😉

━━━━━━━━━━━━━━
👯 *ARKADAŞINI GETİR*

Rezervasyonunuzu yaptıktan sonra çıkan yeşil *"Arkadaşını Getir"* butonuyla dersi tek tıkla arkadaşınıza WhatsApp'tan gönderebilirsiniz. Birlikte çalışmak daha keyifli! 💪

━━━━━━━━━━━━━━
👀 Derse katılan diğer arkadaşlarımızın ad ve soyadının ilk harfini (ör. "Mehmet K.") linkte görebilirsiniz.

Sorularınız için: 📞 {TELEFON}

Haftaya ilk programla görüşmek üzere! 🧘‍♀️✨
*{STUDYO}*`;

const WEEKLY = `📅 *{STUDYO} – Bu haftanın ders programı yayında!* 🎉
_(Grup Dersi · Düet · Özel Ders)_

Yerinizi şimdiden ayırın, kontenjanlar sınırlı 👇
🔗 {LINK}

🔥 "Son 1 yer!" yazan derslerde acele edin.
🔔 Dolu derslerde "Yer açılırsa haber ver" ile bekleme listesine girebilirsiniz.
❌ İptaller dersten en geç *{IPTAL_SAAT} saat önce*, linkteki "Rezervasyonlarım / İptal" bölümünden.

Görüşmek üzere! 💪
*{STUDYO}*`;

export const DEFAULT_TEMPLATES = { intro_template: INTRO, weekly_template: WEEKLY };

const render = (tpl, v) => tpl
  .replaceAll("{STUDYO}", v.name || "Stüdyomuz")
  .replaceAll("{LINK}", v.link)
  .replaceAll("{IPTAL_SAAT}", String(v.hours ?? 3))
  .replaceAll("{TELEFON}", v.phone || "-");

const TemplateBlock = ({ field, title, desc, saved, vars, onSave }) => {
  const [editing, setEditing] = useState(false);
  const tpl = saved || DEFAULT_TEMPLATES[field];
  const [draft, setDraft] = useState(tpl);
  const text = render(editing ? draft : tpl, vars);
  const id = field.replace("_template", "");

  const copy = () => { navigator.clipboard.writeText(text).then(() => toast.success("Metin kopyalandı, gruba yapıştırabilirsiniz")).catch(() => toast.error("Kopyalanamadı")); };
  const save = async () => { await onSave({ [field]: draft === DEFAULT_TEMPLATES[field] ? "" : draft }); setEditing(false); };
  const reset = async () => {
    if (!window.confirm("Metin varsayılan haline dönsün mü?")) return;
    setDraft(DEFAULT_TEMPLATES[field]);
    await onSave({ [field]: "" });
    setEditing(false);
  };

  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid={`wa-text-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-navy">{title}</p>
        {saved && <span className="text-[11px] rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" data-testid={`wa-text-${id}-custom-badge`}>Düzenlendi</span>}
      </div>
      <p className="text-xs text-grey">{desc}</p>
      {editing ? (
        <>
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={14} className="rounded-xl font-mono text-xs" data-testid={`wa-text-${id}-editor`} />
          <p className="text-[11px] text-grey">Otomatik alanlar: <code>{"{STUDYO}"}</code> <code>{"{LINK}"}</code> <code>{"{IPTAL_SAAT}"}</code> <code>{"{TELEFON}"}</code>. Bunlar kopyalarken gerçek bilgilerle değiştirilir.</p>
        </>
      ) : (
        <pre className="whitespace-pre-wrap text-xs bg-slate-50 rounded-xl p-3 max-h-64 overflow-y-auto font-sans" data-testid={`wa-text-${id}-preview`}>{text}</pre>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="rounded-xl" onClick={copy} data-testid={`wa-text-${id}-copy`}><Copy className="w-4 h-4 mr-1" />Kopyala</Button>
        <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer">
          <Button size="sm" className="rounded-xl bg-[#25D366] hover:bg-[#1ebe5a]" data-testid={`wa-text-${id}-share`}><MessageCircle className="w-4 h-4 mr-1" />WhatsApp'ta Paylaş</Button>
        </a>
        {editing ? (
          <>
            <Button size="sm" variant="outline" className="rounded-xl" onClick={save} data-testid={`wa-text-${id}-save`}><Save className="w-4 h-4 mr-1" />Kaydet</Button>
            <Button size="sm" variant="ghost" className="rounded-xl" onClick={() => { setDraft(tpl); setEditing(false); }} data-testid={`wa-text-${id}-cancel`}>Vazgeç</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" className="rounded-xl" onClick={() => { setDraft(tpl); setEditing(true); }} data-testid={`wa-text-${id}-edit`}><Pencil className="w-4 h-4 mr-1" />Düzenle</Button>
        )}
        {saved && <Button size="sm" variant="ghost" className="rounded-xl text-red-600" onClick={reset} data-testid={`wa-text-${id}-reset`}><RotateCcw className="w-4 h-4 mr-1" />Varsayılana dön</Button>}
      </div>
    </div>
  );
};

export const WhatsAppGroupTexts = ({ s, link, onSave }) => {
  const vars = { name: s.business_name, link, hours: s.cancel_hours, phone: s.phone };
  return (
    <div className="space-y-3 pt-2" data-testid="wa-group-texts">
      <p className="font-semibold text-navy flex items-center gap-2"><Megaphone className="w-4 h-4 text-emerald-600" />WhatsApp Duyuru Metinleri</p>
      <TemplateBlock field="intro_template" title="Tanıtım Metni" desc="Yeni WhatsApp grubunu kurduğunuzda bir kez paylaşın. Rezervasyon ve iptal adımlarını anlatır." saved={s.intro_template} vars={vars} onSave={onSave} />
      <TemplateBlock field="weekly_template" title="Haftalık Program Duyurusu" desc="Her hafta yeni dersleri açtığınızda gruba atın." saved={s.weekly_template} vars={vars} onSave={onSave} />
    </div>
  );
};
